/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Typography, Chip, Box } from '@mui/material';
import { useMemo } from 'react';
import { usePluginsTranslation } from '../../../utils/i18n';
import type { DebugLogEntry } from '../pluginDebugTypes';
import { PreBox } from './PreBox';

interface ScopeStatesSectionProps {
  logEntries: DebugLogEntry[];
  instanceId?: string;
}

export const ScopeStatesSection: React.FC<ScopeStatesSectionProps> = ({
  logEntries,
  instanceId,
}) => {
  const { t } = usePluginsTranslation();
  const latestStates = useMemo(() => {
    const states: Record<string, unknown> = {};
    const filtered = instanceId
      ? logEntries.filter((e) => e.instanceId === instanceId)
      : logEntries;
    // logEntries are newest first, so first match per scope is latest
    for (const entry of filtered) {
      if (entry.type === 'scope-update' && !states[entry.scope]) {
        states[entry.scope] = entry.data;
      }
    }
    return states;
  }, [logEntries, instanceId]);

  return (
    <Box data-testid="plugin-debug-scope-states" sx={{ px: 2, py: 1 }}>
      <Typography gutterBottom variant="subtitle2">
        {instanceId
          ? t('scope-states-for-instance', { instanceId })
          : t('scope-states')}
      </Typography>
      {Object.keys(latestStates).length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('no-scope-state-recorded-yet')}
        </Typography>
      ) : (
        Object.entries(latestStates).map(([scope, data]) => (
          <Box key={scope} sx={{ mb: 1 }}>
            <Chip
              label={scope}
              size="small"
              sx={{ mb: 0.5 }}
              variant="outlined"
            />

            <PreBox data={data} />
          </Box>
        ))
      )}
    </Box>
  );
};
